import { Modal, TextField, Autocomplete } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { postAPICall } from "lib/callApi";
import { CONVERSION_FUNNEL_DATE_FILTER_OPTIONS, FILTER_PARAMS } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import Loader from "utils/Loader";
import FilterAutocomplete from "../FilterAutocomplete";

function ConversionFilterModal({ style, open, handleClose, filters, setFilters }) {
  const [modalFilters, setModalFilters] = useState(filters);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState({});

  const syncOptionsAndFilter = async (_filters) => {
    try {
      setIsLoading(true);
      const response = await postAPICall(FILTER_PARAMS, true, {
        filters: {
          filterLocations: _filters?.location?.map((lo) => lo.id) || [],
          filterTeams: _filters?.team?.map((lo) => lo.id) || [],
          filterRoles: _filters?.role?.map((lo) => lo.id) || [],
          filterUsers: _filters?.user?.map((lo) => lo.userCompanyLinkId) || [],
          filterReportsTo: _filters?.reportsTo?.map((lo) => lo.id) || [],
          dateFilter: _filters?.dateFilter,
        },
      });
      if (response?.data?.success) {
        const user = response.data.data.users;
        const { reportsTo } = response.data.data;
        const role = response.data.data.roles;
        const team = response.data.data.teams;
        const teamLocation = response.data.data.locations;
        const optionObj = {
          team,
          location: teamLocation,
          user,
          role,
          reportsTo,
          dateFilter: CONVERSION_FUNNEL_DATE_FILTER_OPTIONS,
        };
        const updatedFilter = {};
        Object.keys(optionObj).forEach((key) => {
          if (Array.isArray(optionObj[key]) && Array.isArray(_filters[key])) {
            const arr1 = optionObj[key].map((obj) => obj.id);
            const arr2 = _filters[key];
            updatedFilter[key] = arr2.filter((arr2Obj) => arr1.includes(arr2Obj.id));
          }
        });
        if (CONVERSION_FUNNEL_DATE_FILTER_OPTIONS.includes(_filters.dateFilter)) {
          updatedFilter.dateFilter = _filters.dateFilter;
        }
        setModalFilters({ ...updatedFilter });
        setOptions(optionObj);
      } else if (response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              response?.response?.data?.message || response.message || "Could not retrieve data!",
          }),
          {
            variant: "custom",
          }
        );
      } else {
        handleClose();
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not retrieve data!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const handleApplyClick = () => {
    if (!isLoading) {
      syncOptionsAndFilter(modalFilters);
    }
  };

  const handleDeleteClick = (key, filter) => {
    if (!isLoading) {
      const newFilters = { ...modalFilters, [key]: filter };
      setModalFilters(newFilters);
      syncOptionsAndFilter(newFilters);
    }
  };

  useEffect(() => {
    if (open) {
      syncOptionsAndFilter(modalFilters);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <MDBox sx={{ position: "relative", pointeEvents: isLoading && "none" }}>
          <Loader loading={isLoading} zIndex="10000" />
          <MDTypography
            sx={{ marginBottom: "1rem" }}
            id="modal-modal-title"
            variant="h5"
            component="h2"
          >
            Conversion Funnel Filters
          </MDTypography>
          <MDBox>
            <MDButton
              color="secondaryBlue"
              onClick={() => {
                syncOptionsAndFilter({
                  dateFilter: "This Week",
                  location: [],
                  team: [],
                  user: [],
                  role: [],
                  reportsTo: [],
                });
              }}
              sx={{ mb: 2 }}
            >
              Reset Filters
            </MDButton>
          </MDBox>
          <MDBox mb={2} sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Autocomplete
              disablePortal
              disableClearable
              value={modalFilters?.dateFilter}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, dateFilter: filter }));
              }}
              options={options.dateFilter || []}
              renderInput={(params) => <TextField {...params} label="Date Filter" />}
            />

            <FilterAutocomplete
              field="location"
              options={options.location || []}
              label="Location"
              value={modalFilters?.location}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, location: filter }));
              }}
            />

            <FilterAutocomplete
              options={options.team || []}
              label="Team"
              field="team"
              value={modalFilters.team || []}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, team: filter }));
              }}
            />
            <FilterAutocomplete
              label="Role"
              field="role"
              value={modalFilters.role || []}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, role: filter }));
              }}
              options={options?.role || []}
            />
            <FilterAutocomplete
              label="User"
              field="user"
              value={modalFilters?.user || []}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, user: filter }));
              }}
              options={options?.user || []}
            />
            <FilterAutocomplete
              label="Reports To"
              field="reportsTo"
              value={modalFilters?.reportsTo?.length ? modalFilters.reportsTo : []}
              onApplyClick={handleApplyClick}
              onDeleteClick={handleDeleteClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, reportsTo: filter }));
              }}
              options={options?.reportsTo?.length ? options?.reportsTo : []}
            />
          </MDBox>
          <MDBox sx={{ display: "flex", justifyContent: "flex-end" }}>
            <MDBox>
              <MDButton
                color="gray80"
                onClick={handleClose}
                sx={{ mt: 2, mr: 1, position: "relative" }}
              >
                Close
              </MDButton>
              <MDButton
                color="primaryRed"
                onClick={() => {
                  setFilters(modalFilters);
                  handleClose();
                }}
                sx={{ mt: 2, mr: 1, position: "relative" }}
              >
                Apply
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default ConversionFilterModal;
